import { atom, useAtomValue } from "jotai"
import { forEach } from "lodash"
import { useMemo, useRef } from "react"

import { userAtoms } from "#atoms"
import { USER_ROLES } from "#customTypes/composedTypes/authTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useStableIds } from "#hooks/jotai/useSubscription"

/**
 * User hooks - Read-only from Jotai atoms
 *
 * FirebaseContextProvider already calls useAllDocs for users,
 * so these hooks just read from the already-populated atoms.
 * NO additional subscriptions are created.
 */

export const useGetAllUsers = (): UserConstructor[] => {
  return useAtomValue(userAtoms.allDocsAtom)
}

export const useGetSomeUsers = (ids: Uuid[]): UserConstructor[] => {
  const stableIds = useStableIds(ids)
  const usersAtom = useMemo(
    () =>
      atom((get) =>
        stableIds
          .map((id) => get(userAtoms.docByIdAtom(id)))
          .filter((doc): doc is UserConstructor => doc !== null)
      ),
    [stableIds]
  )

  return useAtomValue(usersAtom)
}

export const useGetTeachers = (): UserConstructor[] => {
  const allUsers = useGetAllUsers()

  return useMemo(() => {
    return allUsers.filter((u) => u.role !== USER_ROLES.DISABLED)
  }, [allUsers])
}

export const useGetOneUser = (
  id: Uuid | undefined | null
): UserConstructor | null => {
  return useAtomValue(userAtoms.docByIdAtom(id ?? null))
}

// Memoize this as much as possible
// This is never checked before users are fully fetched
export const useGetUserByPinHash = (): Record<string, UserConstructor> => {
  const userRef = useRef<Record<string, UserConstructor>>({})
  const allUsers = useGetAllUsers()

  return useMemo(() => {
    const diff: Record<string, UserConstructor> = {}

    forEach(allUsers, (user) => {
      if (user.pinHash && userRef.current[user.id]?.pinHash !== user.pinHash) {
        diff[user.pinHash] = user
      }
    })

    if (Object.keys(diff).length > 0) {
      userRef.current = { ...userRef.current, ...diff }
    }
    return userRef.current
  }, [allUsers])
}
